import {AfterViewInit, Component, EventEmitter, Input, Output} from '@angular/core';
import {Dom, Element, SVG} from '@svgdotjs/svg.js';
import {
  SynopticConfiguration,
  SynopticConfigurationComponent
} from '../../../../../model/synoptics/synoptic-configuration-types';
import {SynopticFile} from '../../../../../model/synoptics/synoptic-file';
import {SynopticsService} from '../../../../../services/synoptics.service';
import TinyColors from 'tinycolor2';

@Component({
  selector: 'app-synoptics-form-visualization',
  templateUrl: './synoptics-form-visualization.component.html',
  styleUrls: ['./synoptics-form-visualization.component.scss']
})
export class SynopticsFormVisualizationComponent implements AfterViewInit {

  @Input() currentComponent: SynopticConfigurationComponent;
  @Input() synopticFiles: SynopticFile[];
  @Output() onLoad: EventEmitter<boolean>;

  public configuration: SynopticConfiguration;

  public highlightColor: string;
  public lastHoveredElement: Element;
  public lastHoveredElementCss: Partial<CSSStyleDeclaration>;
  public lastSelectedElement: Element;
  public lastSelectedElementCss: Partial<CSSStyleDeclaration>;
  public lastSelectedElementId: string;

  public currentElement: Element;
  public elementsWithId: { id: string }[];

  public svgLoaded: boolean = false;

  constructor(private synopticsService: SynopticsService) {
    this.highlightColor = '#DD2222';
    this.elementsWithId = [];
    this.onLoad = new EventEmitter<boolean>(false);
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.readCurrentSynopticFile(500);
    });
  }

  public changeComponent(component: SynopticConfigurationComponent): void {
    if (component.name !== this.currentComponent.name) {
      this.currentComponent = component;
      this.lastSelectedElement = undefined;
      this.lastHoveredElementCss = undefined;
      this.lastSelectedElementId = undefined;
      this.readCurrentSynopticFile(500);
    }
  }

  public selectElementById(elementId: string): void {
    const found: Dom = this.currentElement.findOne('#' + elementId);
    if (found) {
      this.onElementClick(found);
    }
  }

  public readCurrentSynopticFile(height: number): void {
    this.setLoadStatus(false);

    const file: SynopticFile = this.synopticFiles.find(f => f.name === this.currentComponent.file);

    if (file) {
      let draw;
      if (this.currentElement) {
        draw = this.currentElement.replace(SVG().size('100%', height));
      } else {
        draw = SVG().addTo('#base').size('100%', height);
      }
      this.currentElement = draw.svg(this.synopticsService.decodeSVG(file.data).dataDecoded).get(0);
      this.currentElement.attr({width: null, height: null});
      if (this.currentElement.height() > height) {
        this.currentElement.height(height);
      }
      this.currentElement.mouseover((event: MouseEvent) => this.onElementMouseOver(event.target));
      this.currentElement.click((event: MouseEvent) => this.onElementClick(event.target));
      this.buildElementsWithIdList(this.currentElement);
    }
    this.setLoadStatus(true);
  }

  private buildElementsWithIdList(parentElement: Element): void {
    this.elementsWithId.length = 0;
    this.elementsWithId.push(...parentElement
      .find('*')
      .filter(element => !!element.node.id)
      .map(element => ({id: element.id()})));
  }

  public onElementMouseOver(target: EventTarget | Element): void {
    this.restoreLastHoveredElement();

    if (this.elementCanBeInteractedWith(target)) {
      const element = SVG(target);
      if (element.remember('selected')) {
        return;
      }

      this.lastHoveredElement = element;
      if (!this.lastHoveredElement.css('fill')) {
        this.lastHoveredElement.css('fill', 'black');
      }
      this.lastHoveredElementCss = element.css();

      element.remember('hovered', true);
      element.css('fill', TinyColors(element.css('fill')).darken(10).toHexString());
      element.css('transition', '0.2s');
      element.css('cursor', 'pointer');
    }
  }

  public onElementClick(target: EventTarget | Element): void {
    this.restoreLastClickedElement();

    if (this.elementCanBeInteractedWith(target)) {
      const element = SVG(target);
      if (element.id() === this.lastSelectedElement?.id()) {
        this.lastSelectedElement = undefined;
        this.lastHoveredElementCss = undefined;
        this.lastSelectedElementId = undefined;
        return;
      }

      this.lastSelectedElement = element;
      if (!this.lastSelectedElement.css('fill')) {
        this.lastSelectedElement.css('fill', 'black');
      }
      this.lastSelectedElementCss = element.remember('hovered') ? this.lastHoveredElementCss : element.css();
      this.lastSelectedElementId = element.id();

      element.remember('selected', true);
      element.forget('hovered');
      element.css('fill', this.highlightColor);

    } else {
      this.lastSelectedElement = undefined;
      this.lastHoveredElementCss = undefined;
      this.lastSelectedElementId = undefined;
    }
  }

  public restoreLastHoveredElement(): void {
    if (this.lastHoveredElement?.remember('hovered')) {
      this.lastHoveredElement.attr('style', '');
      this.lastHoveredElement.css(this.lastHoveredElementCss);
      this.lastHoveredElement.forget('hovered');
    }
  }

  public restoreLastClickedElement(): void {
    if (this.lastSelectedElement?.remember('selected')) {
      this.lastSelectedElement.attr('style', '');
      this.lastSelectedElement.css(this.lastSelectedElementCss);
      this.lastSelectedElement.forget('selected');
    }
  }

  private elementCanBeInteractedWith(element: any): boolean {
    return element?.id;
  }

  public onHighlightColorChange(event: any): void {
    this.highlightColor = event;
    const element = this.lastSelectedElement;
    this.onElementClick(element);
    this.onElementClick(element);
  }

  private setLoadStatus(status: boolean): void {
    this.svgLoaded = status;
    this.onLoad.emit(status);
  }

}
